import auth0 from 'auth0-js';
import {
    getValue4Lang,
    isLightMode,
    isEmpty,
    clearCache,
    setAuthenticatedLocationHash,
    getUrlHashValue,
    combineUrl,
    getErrorParams,
    getAuthCurrentLang,
    parseQueryString,
    parseUrlHash,
    getQueryStringValue,
    getCacheUserInfo,
    getAppSource,
    getCacheAuthState,
    setCacheAuthState,
} from '~/common';
import { exceptionLog } from '~/client';
import { CTMModal } from '~/components';
import { store } from '~/index.js';
import enUsMessages from '~/locales/en-us/messages';
import zhCnMessages from '~/locales/zh-cn/messages';
import zhTwMessages from '~/locales/zh-tw/messages';
import jaJpMessages from '~/locales/ja-jp/messages';

const formatMessage = ({ id, values }) => getValue4Lang(enUsMessages, zhCnMessages, zhTwMessages, jaJpMessages)[id].replace('{0}', Object.values(values || {})[0]);

export const PREFIX = 'ctmsmartportal';
export const ACCESS_TOKEN = `${PREFIX}_access_token`;
export const ID_TOKEN = `${PREFIX}_id_token`;
export const EXPIRES_AT = `${PREFIX}_expires_at`;
export const SCOPES = `${PREFIX}_scopes`;
export const LOGOUT_URL = `${PREFIX}_logout_url`;
export const CHECK_SESSION_AT = `${PREFIX}_check_session_at`;

export default class Auth {
    tokenRenewalLoading = false;
    tokenRenewalQueue = [];
    auth0;
    config;
    authExpireTimeout;

    constructor(config) {
        this.tokenRenewalQueue = [];
        this.config = config;
        this.auth0 = new auth0.WebAuth({
            domain: this.config.authDomain,
            clientID: this.config.authClientID,
            redirectUri: this.config.authRedirectUri,
            responseType: 'token id_token',
            scope: 'openid profile email',
            leeway: 300
        });
        this.isAuthenticated() && this.scheduleAuthExpire();
    }

    login(options) {
        //https://github.com/auth0/lock/tree/master/src/i18n
        const authLang = getValue4Lang('en', 'zh', 'zh-TW', 'ja', getAuthCurrentLang());
        //https://github.com/auth0/lock
        this.auth0.authorize({
            ...options,
            language: authLang,
            device: isLightMode() ? 'portal-light' : 'portal-dark',
            application_url: this.config.localhostUrl,
            reset_pwd_url: this.config.resetPasswordRoute,
            app_source: getAppSource(),
            app_name: this.config.appName,
        });
    }

    extSSOLogin(options) {
        this.auth0.authorize({
            connection: options.code,
            redirectUri: (this.config.authRedirectUri + '?esp=1'),
            nonce: this.config.authExternalLinkKey,
            state: this.config.authExternalLinkKey,
            app_name: this.config.appName,
        });
    }

    autoLogin(email, pwd, auth0Connection, redirectUrl) {
        var options = {
            domain: this.config.authDomain,
            clientID: this.config.authClientID,
            redirectUri: this.config.authRedirectUri,
            responseType: 'token id_token',
            scope: 'openid profile email',
            realm: auth0Connection,
        };

        var webAuth = new auth0.WebAuth(options);
        const state = new Date().getTime().toString();
        setCacheAuthState({ state, redirectUrl });
        webAuth.login({
            username: email,
            password: pwd,
            state,
        }, function (err, resp) {
            const params = {
                ...getErrorParams(new Error('Auth0_LoginFail'), JSON.stringify({ errObj: err, errRes: resp, opts: options }, null, 4)),
                userEmail: email,
                userName: email,
            };
            exceptionLog(params);
        });
    }

    handleAuthentication(callback) {
        var hashOptions = { __enableIdPInitiatedLogin: true };
        var extContextId = getUrlHashValue(window.location.href, "ext_context_id") || '';
        var useExtSsoPage = getQueryStringValue(window.location.search, "esp") == 1;
        var route = getUrlHashValue(window.location.href, "route") || getQueryStringValue(window.location.href, "route") || '';
        var authContext = getUrlHashValue(window.location.href, "authContext") || getQueryStringValue(window.location.href, "authContext") || '';

        if (!isEmpty(this.config.authExternalLinkKey)) {
            if (!isEmpty(extContextId) || useExtSsoPage) {
                hashOptions = { "state": this.config.authExternalLinkKey, "nonce": "" };
            }
            else {
                if (!isEmpty(document.referrer) && document.referrer.toLowerCase().indexOf(this.config.localhostUrl.toLowerCase()) < 0) {
                    var dcLinkKey = window.atob(this.config.authExternalLinkKey).toLowerCase();
                    if (document.referrer.toLowerCase().indexOf(dcLinkKey) > 0) {
                        hashOptions = { "state": this.config.authExternalLinkKey, "nonce": this.config.authExternalLinkKey };
                    }
                }
            }
        }


        this.auth0.parseHash(hashOptions, (err, authResult) => {
            if (authResult && authResult.accessToken && authResult.idToken) {
                setAuthenticatedLocationHash(window.location.hash);
                const userInfo = this.setSession(authResult, {});
                const stateObj = getCacheAuthState();
                const isAutoLogin = Boolean(stateObj.state && authResult.state == stateObj.state);
                const authUser = { ...userInfo, extContextId, route, authContext, isAutoLogin, autoLoginUrl: isAutoLogin ? stateObj.redirectUrl : '' };
                typeof callback === 'function' && callback(authUser);
            } else if (err) {
                const params = {
                    ...getErrorParams(new Error('Auth0_ParseHashFail'), JSON.stringify({
                        errObj: err,
                        authRes: authResult,
                        opts: hashOptions,
                        href: window.location.href,
                        query: parseQueryString(),
                        hash: parseUrlHash(window.location.hash),
                    }, null, 4)),
                    userEmail: '',
                    userName: '',
                };
                if ('sendBeacon' in navigator) {
                    navigator.sendBeacon(combineUrl(this.config.obtApiUrl, 'Common/V1/json/ExceptionLog'), JSON.stringify(params))
                } else {
                    exceptionLog(params);
                }
                this.config.isDebug && console.log(err);
                this.clearData(false);
                if (err.error === 'access_denied') {
                    this.logout();
                    return;
                }
                if (this.config.isDebug) {
                    alert(`${err.error}. Check the console for further details.(show in debug mode)`);
                }
                this.logout();
            }
        });
    }

    setSession(authResult, { isRenewal = false, renewServerDelay }) {
        // Set the time that the access token will expire at
        let expiresAt = JSON.stringify((authResult.expiresIn * 1000) + new Date().getTime());
        let logoutUrl = "";
        // If there is a value on the `scope` param from the authResult,
        // use it to set scopes in the session for the user. Otherwise
        // use the scopes as requested. If no scopes were requested,
        // set it to nothing
        if (this.config.externalSsoIdentifier) {
            let identifier = this.config.externalSsoIdentifier.split(',');
            logoutUrl = (identifier.some(i => authResult.idTokenPayload.sub.includes(i)) && combineUrl(this.config.localhostUrl, 'sign-out')) || "";
        }
        const scopes = authResult.scope || '';
        const userInfo = this.formatUser(authResult.idTokenPayload);
        localStorage.setItem(ACCESS_TOKEN, authResult.accessToken);
        localStorage.setItem(ID_TOKEN, authResult.idToken);
        localStorage.setItem(EXPIRES_AT, expiresAt);
        localStorage.setItem(SCOPES, JSON.stringify(scopes));
        localStorage.setItem(LOGOUT_URL, logoutUrl);
        // if (!isRenewal) {
        //   localStorage.setItem(LOGOUT_URL, logoutUrl);
        // } else {
        //   localStorage.setItem(LOGOUT_URL, "");
        // }
        //localStorage.setItem(DEBTOR_ID, authResult.idTokenPayload['https://www.ctmsmart.com/debtor']);
        // schedule a token renewal
        this.scheduleRenewal(renewServerDelay || authResult.expiresIn * 1000);
        this.scheduleAuthExpire();
        return userInfo;
    }

    logout() {
        var logoutUrl = localStorage.getItem(LOGOUT_URL);
        // Clear access token and ID token from local storage

        if (isEmpty(logoutUrl)) {
            const returnTo = combineUrl(this.config.localhostUrl, 'login');
            this.auth0.logout({ "returnTo": returnTo });
            // this.auth0.logout({ returnTo });
            // this.auth0.logout({ "federated": 1 });
        } else {
            this.auth0.logout({ "returnTo": logoutUrl });
            // this.auth0.logout({ "returnTo": logoutUrl, "federated": 1 });
        }
        this.clearData(false);
    }

    clearData(clearLogoutUrl = true) {
        localStorage.removeItem(ACCESS_TOKEN);
        localStorage.removeItem(ID_TOKEN);
        localStorage.removeItem(EXPIRES_AT);
        localStorage.removeItem(CHECK_SESSION_AT);
        localStorage.removeItem(SCOPES);
        if (clearLogoutUrl) {
            localStorage.removeItem(LOGOUT_URL);
        }
        clearCache();
        this.tokenRenewalLoading = false;
        this.tokenRenewalQueue = [];
    }

    renewToken() {
        return new Promise(resolve => {
            if (this.tokenRenewalLoading) {
                this.tokenRenewalQueue.push(resolve);
                return;
            }
            this.tokenRenewalLoading = true;
            this.auth0.checkSession({},
                (err, result) => {
                    this.tokenRenewalLoading = false;
                    if (err) {
                        console.error(
                            `Could not get a new token (${err.error}: ${err.error_description}).`
                        );

                        const logoutErrorCode = 'login_required';

                        if ((err.code && err.code == logoutErrorCode) || (err.error && err.error == logoutErrorCode)) {
                            localStorage.removeItem(ACCESS_TOKEN);
                            window.clearTimeout(this.authExpireTimeout);
                            CTMModal.error({
                                className: 'ctm-modal-no-close-icon',
                                centered: true,
                                content: formatMessage({ id: 'timeout_tip' }) + (this.config.isDebug ? ' (AU0050)' : ''),
                                onOk: () => {
                                    this.clearData(false);
                                    window.location.href = this.config.localhostUrl;
                                },
                            });
                        }
                    } else {
                        if (result && result.accessToken && result.idToken) {
                            const authEmail = result.idTokenPayload.email;
                            const user = store.getState().user;
                            if (authEmail.toLowerCase() != user.email.toLowerCase()) {
                                window.clearTimeout(this.authExpireTimeout);
                                CTMModal.error({
                                    className: 'ctm-modal-no-close-icon',
                                    centered: true,
                                    content: formatMessage({ id: 'diff_user_tip_fmt', values: [authEmail] }),
                                    onOk: () => this.login(),
                                    okText: formatMessage({ id: 'refresh' }),
                                });
                                return;
                            }
                            this.setSession(result, { isRenewal: true, renewServerDelay: user.checkSessionInterval });
                            resolve();
                            while (this.tokenRenewalQueue.length) {
                                const func = this.tokenRenewalQueue.shift();
                                func();
                            };
                            console.log(`Successfully renewed auth!`);
                        }
                        else {
                            localStorage.removeItem(ACCESS_TOKEN);
                        }
                    }
                }
            );
        });
    }

    scheduleRenewal(serverDelay) {
        let checkSessionAt = JSON.stringify(serverDelay + new Date().getTime());
        localStorage.setItem(CHECK_SESSION_AT, checkSessionAt);
    }

    scheduleAuthExpire() {
        window.clearTimeout(this.authExpireTimeout);
        const expiresAt = JSON.parse(localStorage.getItem(EXPIRES_AT));
        const delay = expiresAt - Date.now();
        if (delay > 0) {
            this.authExpireTimeout = setTimeout(() => {
                CTMModal.error({
                    className: 'ctm-modal-no-close-icon',
                    centered: true,
                    content: formatMessage({ id: 'timeout_tip' }) + (this.config.isDebug ? ' (AU0050)' : ''),
                    onOk: () => {
                        this.clearData(false);
                        window.location.href = this.config.localhostUrl;
                    },
                });
            }, delay);
        }
    }

    isAuthenticated() {
        // Check whether the current time is past the
        // access token's expiry time
        let expiresAt = JSON.parse(localStorage.getItem(EXPIRES_AT));
        return new Date().getTime() < expiresAt;
    }

    isNeedCheckSession() {
        let expiresAt = JSON.parse(localStorage.getItem(CHECK_SESSION_AT));
        return new Date().getTime() > expiresAt;
    }

    userHasScopes(scopes) {
        const grantedScopes = (JSON.parse(localStorage.getItem(SCOPES)) || '').split(' ');
        return scopes.every(scope => grantedScopes.includes(scope));
    }

    formatUser(orgUser) {
        const { nickname, name, email, picture, sub } = orgUser;

        return { nickname, name, email, picture, sub };
    }
}
